import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextAWS';
import { getAvailableSeasons, getCurrentSeason, formatSeasonDateRange, getSeasonDayCount } from '../utils/seasonManager';
import { getDraftTimeParts, formatDraftTime, DRAFT_TIME_MINUTE_OPTIONS } from '../utils/scheduleUtils';
import leagueApiService from '../services/leagueApi';

function CreateLeague() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const defaultSeason = getCurrentSeason();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    maxTeams: 12,
    scoringType: 'Standard',
    seasonYear: defaultSeason.year,
    draftDate: '',
    draftTime: '',
    isPrivate: false,
    teamName: `${(user.fullName || user.name || 'User')}'s Team`,
    teamAbbreviation: '',
    logoEmoji: '⚾',
    logoColor: '#007bff'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const availableSeasons = getAvailableSeasons();
  const selectedSeason = availableSeasons.find(s => s.year === parseInt(formData.seasonYear)) || defaultSeason;
  
  const logoEmojis = ['⚾', '🏆', '🔥', '⚡', '🌟', '💪', '🎯', '👑', '🦅', '🐻', '🐯', '🦁'];
  const logoColors = [
    { name: 'Blue', value: '#007bff' },
    { name: 'Red', value: '#dc3545' },
    { name: 'Green', value: '#28a745' },
    { name: 'Orange', value: '#fd7e14' },
    { name: 'Purple', value: '#6f42c1' },
    { name: 'Teal', value: '#20c997' },
    { name: 'Navy', value: '#001f3f' },
    { name: 'Crimson', value: '#85144b' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!formData.name || !formData.draftDate || !formData.draftTime) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // Prepare league data for API
      const leagueData = {
        name: formData.name,
        description: formData.description,
        teamName: formData.teamName,
        userId: user.userId || user.id || user.sub,
        userName: user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        userEmail: user.email,
        settings: {
          maxTeams: parseInt(formData.maxTeams),
          scoringType: formData.scoringType,
          rosterSize: 40,
        },
        season: {
          startDate: selectedSeason.startDate,
          endDate: selectedSeason.endDate,
          year: parseInt(selectedSeason.year),
        },
        draftDate: formData.draftDate,
        draftTime: formData.draftTime,
        isPrivate: formData.isPrivate,
        logoEmoji: formData.logoEmoji,
        logoColor: formData.logoColor,
        teamAbbreviation: formData.teamAbbreviation?.trim().toUpperCase() || undefined,
      };

      console.log('Creating league with data:', leagueData);

      // Call API to create league
      const result = await leagueApiService.createLeague(leagueData);
      
      console.log('League created:', result);

      if (result.success) {
        setSuccess('League created successfully! Redirecting to settings...');
        // Navigate to the league settings page
        const newLeagueId = result.league?.leagueId || result.league?.id;
        setTimeout(() => {
          navigate(`/league/${newLeagueId}/settings`);
        }, 1500);
      } else {
        throw new Error(result.error || 'Failed to create league');
      }
    } catch (error) {
      console.error('Error creating league:', error);
      
      // Check if it's a backend deployment issue
      if (error.message && error.message.includes('Backend API not available')) {
        setError(
          '⚠️ Backend not deployed yet!\n\n' +
          'To create leagues, you need to deploy the backend:\n' +
          '1. Open a new terminal\n' +
          '2. Run: npm run sandbox\n' +
          '3. Wait for deployment (3-5 minutes)\n' +
          '4. Refresh this page and try again'
        );
      } else {
        setError(error.message || 'Failed to create league. Please try again.');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="container">
      <div className="card">
        <h1>Create New League</h1>
        <p>Set up your fantasy baseball league and invite friends to join.</p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Team Customization Section */}
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: 'var(--surface-muted)', borderRadius: 'var(--radius-md)' }}>
            <h3 className="text-player-name" style={{ marginTop: 0 }}>Your Team</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', alignItems: 'end' }}>
              <div className="form-group">
                <label htmlFor="teamName">Team Name *</label>
                <input
                  type="text"
                  id="teamName"
                  name="teamName"
                  className="form-control"
                  value={formData.teamName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your team name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="teamAbbreviation">Team Abbreviation (up to 4 characters)</label>
                <input
                  type="text"
                  id="teamAbbreviation"
                  name="teamAbbreviation"
                  className="form-control"
                  value={formData.teamAbbreviation}
                  onChange={(e) => setFormData({...formData, teamAbbreviation: e.target.value.slice(0, 4).toUpperCase()})}
                  placeholder="e.g., TEAM"
                  maxLength={4}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              
              <div className="form-group">
                <label>Logo Emoji</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {logoEmojis.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setFormData({...formData, logoEmoji: emoji})}
                      style={{
                        width: '50px',
                        height: '50px',
                        fontSize: '24px',
                        border: formData.logoEmoji === emoji ? '3px solid var(--primary)' : '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: 'var(--background)',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label>Logo Color</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {logoColors.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({...formData, logoColor: color.value})}
                      title={color.name}
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: color.value,
                        border: formData.logoColor === color.value ? '3px solid var(--foreground)' : '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Team Preview */}
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', display: 'inline-flex', alignItems: 'center', gap: '15px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '10%',
                backgroundColor: formData.logoColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                color: 'white',
                overflow: 'hidden',
              }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transform: 'scale(0.8)' }}>
                  {formData.logoEmoji}
                </span>
              </div>
              <div>
                <div className="text-player-name">{formData.teamName || 'Your Team'}</div>
                <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
                  {formData.teamAbbreviation && <span>{formData.teamAbbreviation} • </span>}
                  Team Preview
                </div>
              </div>
            </div>
          </div>
          
          {/* League Settings Section */}
          <h3 className="text-player-name" style={{ marginBottom: '20px' }}>League Settings</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label htmlFor="name">League Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter league name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="maxTeams">Maximum Teams</label>
              <select
                id="maxTeams"
                name="maxTeams"
                className="form-control"
                value={formData.maxTeams}
                onChange={handleInputChange}
              >
                <option value={8}>8 Teams</option>
                <option value={10}>10 Teams</option>
                <option value={12}>12 Teams</option>
                <option value={14}>14 Teams</option>
                <option value={16}>16 Teams</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="scoringType">Scoring Type</label>
              <select
                id="scoringType"
                name="scoringType"
                className="form-control"
                value={formData.scoringType}
                onChange={handleInputChange}
              >
                <option value="Standard">Standard</option>
                <option value="Points">Points</option>
                <option value="Categories">Categories</option>
                <option value="Head-to-Head">Head-to-Head</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="seasonYear">MLB Season *</label>
              <select
                id="seasonYear"
                name="seasonYear"
                className="form-control"
                value={formData.seasonYear}
                onChange={handleInputChange}
                required
              >
                {availableSeasons.map(season => (
                  <option key={season.year} value={season.year}>
                    {season.name}
                  </option>
                ))}
              </select>
              <div className="hint">
                {formatSeasonDateRange(selectedSeason)} ({getSeasonDayCount(selectedSeason)} days)
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="draftDate">Draft Date *</label>
              <input
                type="date"
                id="draftDate"
                name="draftDate"
                className="form-control"
                value={formData.draftDate}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                max={new Date(new Date(selectedSeason.startDate).getTime() - 24*60*60*1000).toISOString().split('T')[0]}
                required
              />
              <div className="hint">
                Draft must be before season starts ({new Date(selectedSeason.startDate).toLocaleDateString()})
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="draftTime">Draft Time *</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  id="draftTime"
                  aria-label="Draft hour"
                  className="form-control"
                  value={getDraftTimeParts(formData.draftTime).hour}
                  onChange={(e) => {
                    const { minute } = getDraftTimeParts(formData.draftTime);
                    setFormData(prev => ({ ...prev, draftTime: formatDraftTime(Number(e.target.value), minute) }));
                  }}
                  required
                  style={{ minWidth: '80px' }}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                  ))}
                </select>
                <span style={{ fontWeight: '600', color: 'var(--muted)' }}>:</span>
                <select
                  aria-label="Draft minute"
                  className="form-control"
                  value={getDraftTimeParts(formData.draftTime).minute}
                  onChange={(e) => {
                    const { hour } = getDraftTimeParts(formData.draftTime);
                    setFormData(prev => ({ ...prev, draftTime: formatDraftTime(hour, Number(e.target.value)) }));
                  }}
                  style={{ minWidth: '80px' }}
                >
                  {DRAFT_TIME_MINUTE_OPTIONS.map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
              <div className="hint">
                5-minute intervals (e.g. 7:00, 7:05, 7:10)
              </div>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="description">League Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
                placeholder="Describe your league rules, settings, and any special requirements..."
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="leagueVisibility">League Visibility</label>
              <select
                id="leagueVisibility"
                name="leagueVisibility"
                className="form-control"
                value={formData.isPrivate ? 'private' : 'public'}
                onChange={(e) => setFormData(prev => ({ ...prev, isPrivate: e.target.value === 'private' }))}
              >
                <option value="public">Public</option>
                <option value="private">Private (Join by email invite only)</option>
              </select>
              <small className="hint">
                Public leagues can be discovered; private leagues are hidden. All leagues require an email invite to join.
              </small>
            </div>
          </div>

          <div className="card-actions" style={{ marginTop: '30px' }}>
            <button type="submit" className="btn btn-primary">
              Create League
            </button>
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => navigate('/leagues')}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateLeague;

